import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { tenantHref } from "@/lib/tenantPath";
import { useRequireAuth } from "@/components/auth/useRequireAuth";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
// Relative on purpose: pure modules and components, resolvable under Jest without a shim.
import LoadFailure from "../components/ui/LoadFailure";
import usePathwayCatalogue from "../pathways/usePathwayCatalogue";
import { pathwayPath } from "../pathways/learnerModel";
import { PATHWAYS_LEARNER as IDS } from "../constants/testIds/pathwaysLearner";
import JoinForm from "./pathways/JoinForm";
import PathwayCard from "./pathways/PathwayCard";

/**
 * `/parcours` (spec screen 6): the practice's published pathways as cards
 * (`GET /public/pathways`, contract §5.2). No session is needed to browse.
 *
 * Each card states what a visitor weighs before committing (public/pathways/PathwayCard):
 * the type in plain words, the estimated length, the objectives, the price and the places
 * left. "Join the pathway" opens the join form right under its card (one open at a time),
 * which signs the visitor in and enrols them, or sends them to the checkout for a paid
 * pathway. A joined learner lands on the pathway home.
 *
 * "No pathway is open" and "the list could not be loaded" are different answers.
 */
export default function Parcours() {
  const t = useT("public");
  const navigate = useNavigate();
  const auth = useRequireAuth();
  const { data, loading, error, reload } = usePathwayCatalogue();
  const [joining, setJoining] = useState("");

  const toggle = (id) => setJoining((current) => (current === id ? "" : id));
  const enrolled = (id) => navigate(tenantHref(pathwayPath(id)));

  let body;
  if (loading && !data) {
    body = <Loader2 className="w-6 h-6 animate-spin text-eh-muted" aria-hidden="true" />;
  } else if (error) {
    body = (
      <LoadFailure
        message={t("pathways.catalogue.loadFailed")}
        onRetry={reload}
        retryLabel={t("pathways.retry")}
        testId={IDS.catalogueLoadError}
      />
    );
  } else if (data.length === 0) {
    body = (
      <p data-testid={IDS.catalogueEmpty} className="rounded-3xl border border-eh-border-soft bg-eh-cream px-6 py-16 text-center text-eh-muted">
        {t("pathways.catalogue.empty")}
      </p>
    );
  } else {
    body = (
      <ul className="grid gap-6 md:grid-cols-2">
        {data.map((card) => (
          <li key={card.id} className="space-y-3">
            <PathwayCard card={card} onJoin={() => toggle(card.id)} joinOpen={joining === card.id} />
            {joining === card.id && (
              <JoinForm card={card} auth={auth} onEnrolled={enrolled} onFull={reload} />
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-10 max-w-5xl mx-auto px-4 sm:px-6">
        <h1 className="font-display text-4xl sm:text-5xl text-eh-ink leading-tight">{t("pathways.catalogue.heading")}</h1>
        <p className="text-eh-muted mt-4 text-lg max-w-2xl">{t("pathways.catalogue.subheading")}</p>
      </section>
      <section data-testid={IDS.catalogue} className="max-w-5xl mx-auto px-4 sm:px-6 pb-28">
        {body}
      </section>
      {auth.authDialog}
    </ThemedPageLayout>
  );
}
